import { createClient } from '@supabase/supabase-js'

// Supabase configuration
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Database types
export interface Profile {
  id: string
  name: string
  bio: string
  contact: {
    email: string
    phone?: string
    location?: string
  }
  profile_image_url?: string
  created_at: string
  updated_at: string
}

export interface Achievement {
  id: string
  profile_id: string
  title: string
  description: string
  achievement_date: string
  category: string
  level: string
  image_url?: string
  created_at: string
  updated_at: string
}

export interface Education {
  id: string
  profile_id: string
  school: string
  institution?: string
  level: string
  degree?: string
  field_of_study?: string
  location?: string
  start_year: number
  end_year?: number
  start_date?: string
  end_date?: string
  is_current?: boolean
  status?: 'completed' | 'planned' | 'in_progress'
  gpa?: string
  description?: string
  image_url?: string
  created_at: string
  updated_at: string
}

export interface Certificate {
  id: string
  profile_id: string
  certificate_name: string
  issuing_institution: string
  description?: string
  issue_date: string
  expiry_date?: string
  certificate_number?: string
  category: string
  status: 'active' | 'expired' | 'pending' | 'revoked'
  image_url?: string
  created_at: string
  updated_at: string
}

export interface Gallery {
  id: string
  profile_id: string
  title: string
  description?: string
  image_url: string
  sort_order: number
  tags?: string[]
  category?: string
  date_taken?: string
  location?: string
  created_at: string
  updated_at: string
}

// Auth helpers
export const signInWithEmail = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  })
  return { data, error }
}

export const signOut = async () => {
  const { error } = await supabase.auth.signOut()
  return { error }
}

export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser()
  return { user, error }
}

// Storage helpers
export const uploadFile = async (bucket: string, path: string, file: File) => {
  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(path, file, {
      cacheControl: '3600',
      upsert: false
    })
  return { data, error }
}

export const getPublicUrl = (bucket: string, path: string) => {
  const { data } = supabase.storage
    .from(bucket)
    .getPublicUrl(path)
  return data.publicUrl
}

export const deleteFile = async (bucket: string, path: string) => {
  const { data, error } = await supabase.storage
    .from(bucket)
    .remove([path])
  return { data, error }
}

export const listFiles = async (bucket: string, folder: string) => {
  const { data, error } = await supabase.storage
    .from(bucket)
    .list(folder, {
      limit: 100,
      offset: 0
    })
  return { data, error }
}